import { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Pagination } from "shared/ui/pagination";
import { useUserStore } from "entities/user";
import { USER_ROLES } from "shared/config/routes";
import { getEventRecordsRequest } from "pages/lk/api/getEventRecordsRequest";
import { confirmPaymentRequest } from "pages/lk/api/confirmPaymentRequest";
import { cancelPaymentRequest } from "pages/lk/api/cancelPaymentRequest";
import { useHttpRequest } from "shared/lib/hooks/useHttpRequest";
import { formatDateForEvent } from "shared/lib/hooks/useDateFormatting";
import { useMobile } from "shared/lib/hooks";
import { Button } from "shared/ui/button";
import { LK_EVENTS_ROUTE } from "shared/config/routes";
import { UserInfoModal } from "./UserInfoModal";
import { Select } from "shared/ui/select";
import Copy from "shared/assets/images/Copy.svg";
import Back from "shared/assets/images/Back.svg";
import Info from "shared/assets/images/Info.svg";
import styles from "./EventRecords.module.scss";

const PAGE_SIZE = 30;

export const EventRecords = () => {
  const { role } = useUserStore();
  const { eventId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [eventHeader, setEventHeader] = useState("");
  const [eventDate, setEventDate] = useState("");

  const [page, setPage] = useState(1);
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
    total: 0,
    pages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [isUserInfoModal, setIsUserInfoModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const [paymentFilter, setPaymentFilter] = useState("");
  const paymentOptions = [
    { value: "", dropdownLabel: "--", label: "" },
    { value: true, dropdownLabel: "Да", label: "*Да" },
    { value: false, dropdownLabel: "Нет", label: "*Нет" },
  ];

  const newPaymentOptions = [
    { value: true, dropdownLabel: "Да", label: "Да" },
    { value: false, dropdownLabel: "Нет", label: "Нет" },
  ];

  const [paymentValues, setPaymentValues] = useState({});

  const {
    sendRequest: sendConfirmPayment,
    loading: confirmPaymentLoading,
    error: confirmPaymentError,
    clearError: clearConfirmPaymentError,
  } = useHttpRequest(confirmPaymentRequest);

  const {
    sendRequest: sendCancelPayment,
    loading: cancelPaymentLoading,
    error: cancelPaymentError,
    clearError: clearCancelPaymentError,
  } = useHttpRequest(cancelPaymentRequest);

  const isTablet = useMobile("950px");
  const isMobile = useMobile("830px");

  const loadRecords = async (currentEventId, currentPage, payment, signal) => {
    try {
      setLoading(true);
      setError(null);

      const data = await getEventRecordsRequest(
        {
          eventId: currentEventId,
          page: currentPage,
          pageSize: PAGE_SIZE,
          payment,
        },
        { signal },
      );

      if (signal.aborted) return;

      const nextRecords = Array.isArray(data?.records) ? data.records : [];
      setRecords(nextRecords);
      setPagination(
        data?.pagination ?? {
          page: currentPage,
          pageSize: PAGE_SIZE,
          total: 0,
          pages: 1,
        },
      );

      setPaymentValues(
        nextRecords.reduce((acc, record) => {
          acc[record.id] = record.payment;
          return acc;
        }, {}),
      );
    } catch (e) {
      if (e.name !== "AbortError" && !signal.aborted) {
        setError(e);
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  };

  const handlePageChange = (nextPage) => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    setPage(nextPage);
  };

  const openUserInfoModal = (record) => {
    setSelectedRecord(record);
    setIsUserInfoModalOpen(true);
  };

  const handleCopyInfo = async (data) => {
    try {
      await navigator.clipboard.writeText(data);
    } catch (error) {
      console.error("Не удалось скопировать", error);
    }
  };

  const handlePaymentChange = async (recordId, newValue) => {
    const prevValue = paymentValues[recordId] ?? false;

    setPaymentValues((prev) => ({
      ...prev,
      [recordId]: newValue,
    }));

    try {
      if (newValue === true) {
        await sendConfirmPayment(recordId);
        clearConfirmPaymentError();
      } else {
        await sendCancelPayment(recordId);
        clearCancelPaymentError();
      }
    } catch (error) {
      setPaymentValues((prev) => ({
        ...prev,
        [recordId]: prevValue,
      }));
      console.error("Не удалось обновить payment", error);
    }
  };

  useEffect(() => {
    setEventHeader(location.state?.eventHeader || "");
    setEventDate(location.state?.eventDate || "");
  }, [location.state]);

  useEffect(() => {
    if (role !== USER_ROLES.MANAGER || !eventId) return;

    const controller = new AbortController();
    loadRecords(eventId, page, paymentFilter, controller.signal);

    return () => controller.abort();
  }, [role, eventId, page, paymentFilter]);

  if (role !== USER_ROLES.MANAGER) return null;

  return (
    <div className={styles.contentWrapper}>
      {loading && (
        <div className={styles.contentWrapper__stateBlock}>
          <p className={styles.contentWrapper__stateText}>Загрузка...</p>
        </div>
      )}
      {error && (
        <div className={styles.contentWrapper__stateBlock}>
          <p className={styles.contentWrapper__stateText}>Ошибка загрузки</p>
        </div>
      )}
      <div className={styles.contentWrapper__recordsBlock}>
        <div className={styles.contentWrapper__titleBlock}>
          <div>
            <Button
              variant="ghost"
              size="small"
              leftIcon={<img src={Back} alt="back" height={16} width={17} />}
              onClick={() => navigate(LK_EVENTS_ROUTE)}
            />
          </div>
          <div className={styles.contentWrapper__eventInfo}>
            <p className={styles.contentWrapper__eventHeader}>{eventHeader}</p>
            <p className={styles.contentWrapper__eventDate}>
              {formatDateForEvent(eventDate)}
            </p>
          </div>
        </div>

        {!loading && !error && (
          <table className={styles.contentWrapper__table}>
            <colgroup>
              {isMobile ? (
                <>
                  <col style={{ width: "55%" }} />
                  <col style={{ width: "45%" }} />
                </>
              ) : isTablet ? (
                <>
                  <col style={{ width: "50%" }} />
                  <col style={{ width: "40%" }} />
                  <col style={{ width: "10%" }} />
                </>
              ) : (
                <>
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "5%" }} />
                </>
              )}
            </colgroup>
            <thead className={styles.contentWrapper__thead}>
              <tr>
                {isMobile ? (
                  <>
                    <th className={styles.contentWrapper__leftAlightHead}>
                      ФИО
                    </th>
                    <th className={styles.contentWrapper__centerAlightHead}>
                      <Select
                        variant="white"
                        label="Факт оплаты"
                        placeholder=""
                        options={paymentOptions}
                        value={paymentFilter}
                        onChange={setPaymentFilter}
                      />
                    </th>
                  </>
                ) : isTablet ? (
                  <>
                    <th className={styles.contentWrapper__leftAlightHead}>
                      ФИО
                    </th>
                    <th className={styles.contentWrapper__centerAlightHead}>
                      <Select
                        variant="white"
                        label="Факт оплаты"
                        placeholder=""
                        options={paymentOptions}
                        value={paymentFilter}
                        onChange={setPaymentFilter}
                      />
                    </th>
                    <th></th>
                  </>
                ) : (
                  <>
                    <th className={styles.contentWrapper__leftAlightHead}>
                      ФИО
                    </th>
                    <th className={styles.contentWrapper__centerAlightHead}>
                      Номер телефона
                    </th>
                    <th className={styles.contentWrapper__centerAlightHead}>
                      Почтовый адрес
                    </th>
                    <th className={styles.contentWrapper__centerAlightHead}>
                      <Select
                        variant="white"
                        label="Факт оплаты"
                        placeholder=""
                        options={paymentOptions}
                        value={paymentFilter}
                        onChange={setPaymentFilter}
                      />
                    </th>
                    <th></th>
                  </>
                )}
              </tr>
            </thead>

            <tbody className={styles.contentWrapper__tbody}>
              {!loading && !error && records.length === 0 ? (
                <tr>
                  <td colSpan={isMobile ? 2 : isTablet ? 3 : 5}>
                    <div className={styles.contentWrapper__stateBlock}>
                      <p className={styles.contentWrapper__stateText}>
                        Пользователи не найдены
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                records.map((record) => (
                  <tr key={record.id}>
                    {isMobile ? (
                      <>
                        <td
                          className={styles.contentWrapper__leftAlightCell}
                          onClick={() => openUserInfoModal(record)}
                        >
                          {record.fio}
                        </td>
                        <td className={styles.contentWrapper__centerAlightCell}>
                          <Select
                            variant={
                              paymentValues[record.id] === true
                                ? "green"
                                : "red"
                            }
                            label=""
                            placeholder=""
                            options={newPaymentOptions}
                            value={paymentValues[record.id] ?? record.payment}
                            onChange={(value) =>
                              handlePaymentChange(record.id, value)
                            }
                            disabled={
                              confirmPaymentLoading || cancelPaymentLoading
                            }
                          />
                        </td>
                      </>
                    ) : isTablet ? (
                      <>
                        <td
                          className={styles.contentWrapper__leftAlightCell}
                          onClick={() => openUserInfoModal(record)}
                        >
                          {record.fio}
                        </td>
                        <td className={styles.contentWrapper__centerAlightCell}>
                          <Select
                            variant={
                              paymentValues[record.id] === true
                                ? "green"
                                : "red"
                            }
                            label=""
                            placeholder=""
                            options={newPaymentOptions}
                            value={paymentValues[record.id] ?? record.payment}
                            onChange={(value) =>
                              handlePaymentChange(record.id, value)
                            }
                            disabled={
                              confirmPaymentLoading || cancelPaymentLoading
                            }
                          />
                        </td>
                        <td className={styles.contentWrapper__infoCell}>
                          <Button
                            variant="ghost"
                            size="small"
                            leftIcon={
                              <img
                                src={Info}
                                alt="info"
                                height={30}
                                width={30}
                              />
                            }
                            onClick={() => openUserInfoModal(record)}
                          />
                        </td>
                      </>
                    ) : (
                      <>
                        <td
                          className={styles.contentWrapper__leftAlightCell}
                          onClick={() => openUserInfoModal(record)}
                        >
                          {record.fio}
                        </td>
                        <td
                          className={styles.contentWrapper__centerAlightCell}
                          onClick={() => handleCopyInfo(record.phoneNumber)}
                        >
                          <span>
                            {record.phoneNumber}
                            <img
                              src={Copy}
                              alt="copy"
                              className={styles.contentWrapper__copyIcon}
                            />
                          </span>
                        </td>
                        <td
                          className={styles.contentWrapper__centerAlightCell}
                          onClick={() => handleCopyInfo(record.email)}
                        >
                          <span>
                            {record.email}
                            <img
                              src={Copy}
                              alt="copy"
                              className={styles.contentWrapper__copyIcon}
                            />
                          </span>
                        </td>
                        <td className={styles.contentWrapper__centerAlightCell}>
                          <Select
                            variant={
                              paymentValues[record.id] === true
                                ? "green"
                                : "red"
                            }
                            label=""
                            placeholder=""
                            options={newPaymentOptions}
                            value={paymentValues[record.id] ?? record.payment}
                            onChange={(value) =>
                              handlePaymentChange(record.id, value)
                            }
                            disabled={
                              confirmPaymentLoading || cancelPaymentLoading
                            }
                          />
                        </td>
                        <td className={styles.contentWrapper__infoCell}>
                          <Button
                            variant="ghost"
                            size="small"
                            leftIcon={
                              <img
                                src={Info}
                                alt="info"
                                height={30}
                                width={30}
                              />
                            }
                            onClick={() => openUserInfoModal(record)}
                          />
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      <div className={styles.contentWrapper__paginationBlock}>
        <Pagination
          page={pagination.page}
          totalPages={pagination.pages}
          onPageChange={handlePageChange}
        />
      </div>
      <UserInfoModal
        isModalOpen={isUserInfoModal}
        close={() => setIsUserInfoModalOpen(false)}
        toggle={() => setIsUserInfoModalOpen(false)}
        record={selectedRecord}
      />
    </div>
  );
};
